import React from 'react'

/* Group the grid's rows by segment — the catalogue's own grouping ("Cue cards",
   "Improv survival"), which is what a person receiving an order thinks in.
   Shared by At Apartment → Requests and HQ → Daily Sheet, because both render
   the same column set and a core capability belongs on both.

   Modelled on the afterschool games board, with one deliberate difference: that
   board opens ONE group at a time, and here every group starts OPEN and any
   number can be open at once. The segments are lopsided — on the live order 13
   of them hold a single item while two hold 26 and 28 — so one-at-a-time would
   mean 13 clicks to reveal 13 rows.

   ⛔ OFF BY DEFAULT. Every line created before 2026-08-26 has a blank segment
   (the bulk-add dropped it — see `bulk_line_payload`), so switching this on by
   default would wrap an entire order in one nameless group and read as broken.
   Turning it on is a deliberate act until enough lines carry a segment.

   ⚠️ The hook holds STATE ONLY, and `groupEntries` is a plain function called
   later. Both grids compute their `rows` AFTER an
   `if (!requests) return <LinearProgress/>`, and a hook cannot live below that
   — it would crash every render with "Rendered more hooks than during the
   previous render". So the state goes above the early return and the grouping
   is done where the rows actually exist.
*/
export const NO_SEGMENT = '(no segment)'

/* Rows -> a flat list mixing group headers with rows, so neither grid has to
   restructure how it renders a row:
     {kind:'group', key, name, count, open}
     {kind:'row', row}
   Groups come out in first-appearance order, so grouping never silently
   re-sorts the register — a hand-sorted line order is a real thing here. */
export function groupEntries(rows, grouped, collapsed) {
  if (!grouped) return rows.map((row) => ({ kind: 'row', row }))
  const order = []
  const byKey = {}
  rows.forEach((row) => {
    const name = String(row.segment || '').trim() || NO_SEGMENT
    if (!byKey[name]) {
      byKey[name] = { key: name, name, rows: [] }
      order.push(byKey[name])
    }
    byKey[name].rows.push(row)
  })
  const out = []
  order.forEach((g) => {
    const open = !collapsed.has(g.key)
    out.push({ kind: 'group', key: g.key, name: g.name, count: g.rows.length, open })
    if (open) g.rows.forEach((row) => out.push({ kind: 'row', row }))
  })
  return out
}

export function segmentKeysOf(rows) {
  const seen = new Set()
  rows.forEach((row) => seen.add(String(row.segment || '').trim() || NO_SEGMENT))
  return Array.from(seen)
}

export default function useSegmentGroups(storageKey) {
  const [grouped, setGrouped] = React.useState(false)
  const [collapsed, setCollapsed] = React.useState(() => new Set())

  React.useEffect(() => {
    try {
      if (window.localStorage.getItem(storageKey) === '1') setGrouped(true)
    } catch (e) {
      /* storage disabled — stay ungrouped */
    }
  }, [storageKey])

  const toggleGrouped = React.useCallback(() => {
    setGrouped((prev) => {
      const next = !prev
      try {
        window.localStorage.setItem(storageKey, next ? '1' : '0')
      } catch (e) {
        /* non-fatal */
      }
      return next
    })
  }, [storageKey])

  const toggleGroup = React.useCallback(
    (key) =>
      setCollapsed((prev) => {
        const next = new Set(prev)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
      }),
    []
  )

  const expandAll = React.useCallback(() => setCollapsed(new Set()), [])
  const collapseAllOf = React.useCallback(
    (rows) => setCollapsed(new Set(segmentKeysOf(rows))),
    []
  )

  return {
    grouped,
    toggleGrouped,
    collapsed,
    toggleGroup,
    expandAll,
    collapseAllOf
  }
}
